import React, { useState, useEffect } from 'react';
import { tenantService } from '../../services/tenantService';
import { packageService } from '../../services/packageService';
import { Package } from '../../types/package';
import { CreateTenantRequest } from '../../types/tenant';

interface CreateBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 1 | 2 | 3;

/* -- Step Indicator -------------------------------------------- */
const StepIndicator: React.FC<{ currentStep: Step }> = ({ currentStep }) => {
  const steps = [
    { num: 1, label: 'Details' },
    { num: 2, label: 'Plan' },
    { num: 3, label: 'Confirm' },
  ];

  return (
    <div className="flex items-center justify-between px-12 mb-10 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[70%] h-[1px] bg-slate-200 -z-10" />
      {steps.map((step) => (
        <div key={step.num} className="flex flex-col items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              currentStep >= step.num
                ? 'bg-primary text-white ring-4 ring-primary/20'
                : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}
          >
            {currentStep > step.num ? (
              <span className="material-symbols-outlined text-sm">check</span>
            ) : (
              step.num
            )}
          </div>
          <span
            className={`text-[10px] font-black uppercase tracking-widest ${
              currentStep >= step.num ? 'text-primary' : 'text-slate-500'
            }`}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
};

/* -- Section Header -------------------------------------------- */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
    <span className="w-1 h-3 bg-primary rounded-full" />
    {children}
  </h3>
);

/* -- Form Input ------------------------------------------------ */
const FormInput: React.FC<{
  label: string;
  placeholder: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ label, placeholder, type = 'text', value, onChange, disabled }) => (
  <div className="space-y-2">
    <label className="text-xs font-bold text-slate-500">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 text-slate-700 disabled:opacity-50"
    />
  </div>
);

/* -- Create Brand Modal ---------------------------------------- */
const CreateBrandModal: React.FC<CreateBrandModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Form state
  const [brandName, setBrandName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState('');

  // API state
  const [packages, setPackages] = useState<Package[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch packages when step 2 is reached
  useEffect(() => {
    if (currentStep === 2 && packages.length === 0) {
      fetchPackages();
    }
  }, [currentStep, packages.length]);

  const fetchPackages = async () => {
    setPackagesLoading(true);
    try {
      const response = await packageService.getAll();
      if (response.success) {
        setPackages(response.data.filter((p) => p.isActive));
      }
    } catch {
      // Packages couldn't be loaded - user can still proceed without selecting one
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setBrandName('');
    setSubdomain('');
    setAdminFirstName('');
    setAdminLastName('');
    setAdminEmail('');
    setSelectedPackageId('');
    setSubscriptionExpiresAt('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setError(null);
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const isStep1Valid = brandName.trim() && subdomain.trim() && adminFirstName.trim() && adminLastName.trim() && adminEmail.trim();

  const handleCreate = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateTenantRequest = {
        name: brandName.trim(),
        subdomain: subdomain.trim(),
        adminEmail: adminEmail.trim(),
        adminFirstName: adminFirstName.trim(),
        adminLastName: adminLastName.trim(),
        packageId: selectedPackageId || null,
        subscriptionExpiresAt: subscriptionExpiresAt || null,
      };

      const response = await tenantService.create(request);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
          onSuccess();
        }, 2000);
      } else {
        setError(response.message || 'Marka oluşturulurken bir hata oluştu.');
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || 'Marka oluşturulurken bir hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedPackage = packages.find((p) => p.id === selectedPackageId);

  // Success screen
  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
        <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-green-500 text-3xl">check_circle</span>
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2">Marka Oluşturuldu!</h3>
          <p className="text-sm text-slate-500">
            <strong className="text-slate-600">{adminEmail}</strong> adresine davetiye maili gönderildi.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4 overflow-y-auto">
      <div
        className="bg-white w-full max-w-2xl rounded-2xl border border-slate-200 shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-black tracking-tight text-slate-900">Create New Brand</h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <StepIndicator currentStep={currentStep} />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-8 mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-red-500 text-xl">error</span>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="px-8 pb-8 space-y-8 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Details */}
          {currentStep === 1 && (
            <>
              <div className="space-y-4">
                <SectionHeader>Brand Identity</SectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Brand Name"
                    placeholder="e.g. Nexus Corp"
                    value={brandName}
                    onChange={setBrandName}
                  />
                  <FormInput
                    label="Subdomain"
                    placeholder="nexus-corp"
                    value={subdomain}
                    onChange={setSubdomain}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <SectionHeader>Primary Admin Setup</SectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Admin First Name"
                    placeholder="John"
                    value={adminFirstName}
                    onChange={setAdminFirstName}
                  />
                  <FormInput
                    label="Admin Last Name"
                    placeholder="Doe"
                    value={adminLastName}
                    onChange={setAdminLastName}
                  />
                  <div className="md:col-span-2">
                    <FormInput
                      label="Admin Email Address"
                      placeholder="admin@brand.com"
                      type="email"
                      value={adminEmail}
                      onChange={setAdminEmail}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Plan */}
          {currentStep === 2 && (
            <>
              <div className="space-y-4">
                <SectionHeader>Subscription Configuration</SectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500">Select Plan</label>
                    {packagesLoading ? (
                      <div className="w-full bg-slate-50 rounded-lg py-3 px-4 text-sm text-slate-500">
                        Loading packages...
                      </div>
                    ) : (
                      <select
                        value={selectedPackageId}
                        onChange={(e) => setSelectedPackageId(e.target.value)}
                        className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all appearance-none text-slate-600"
                      >
                        <option value="">No Package</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} - {pkg.monthlyPrice > 0 ? `${pkg.monthlyPrice} ${pkg.currency}/mo` : 'Free'}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500">Subscription Expires At</label>
                    <input
                      type="date"
                      value={subscriptionExpiresAt}
                      onChange={(e) => setSubscriptionExpiresAt(e.target.value)}
                      className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all text-slate-600"
                    />
                  </div>
                </div>

                {/* Selected package details */}
                {selectedPackage && (
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mt-4">
                    <p className="text-xs font-bold text-primary mb-3">{selectedPackage.name} Details</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Max Users</p>
                        <p className="text-sm font-bold text-slate-900">{selectedPackage.maxUsers}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">WP Sites</p>
                        <p className="text-sm font-bold text-slate-900">{selectedPackage.maxWordPressSites}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Monthly Content</p>
                        <p className="text-sm font-bold text-slate-900">{selectedPackage.monthlyContentLimit}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <SectionHeader>Review & Confirm</SectionHeader>
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Brand Name</p>
                    <p className="text-sm font-bold text-slate-900">{brandName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Subdomain</p>
                    <p className="text-sm font-bold text-slate-900">{subdomain || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Admin Name</p>
                    <p className="text-sm font-bold text-slate-900">
                      {adminFirstName && adminLastName ? `${adminFirstName} ${adminLastName}` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Admin Email</p>
                    <p className="text-sm font-bold text-slate-900">{adminEmail || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Selected Plan</p>
                    <p className="text-sm font-bold text-primary">
                      {selectedPackage ? selectedPackage.name : 'No Package'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Subscription Expires</p>
                    <p className="text-sm font-bold text-slate-900">
                      {subscriptionExpiresAt || 'Not set'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
                <span className="material-symbols-outlined text-amber-500 text-xl">info</span>
                <div>
                  <p className="text-sm font-bold text-amber-500">Invitation Email</p>
                  <p className="text-xs text-slate-500 mt-1">
                    An invitation email with auto-generated login credentials will be sent to{' '}
                    <strong className="text-slate-600">{adminEmail || 'the admin'}</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 bg-slate-50 border-t border-slate-200 flex items-center justify-between rounded-b-2xl">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                disabled={isSubmitting}
                className="px-6 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-sm">arrow_back</span>
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-6 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={currentStep === 1 && !isStep1Valid}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Brand'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBrandModal;
